<template>
  <div class="lightbox">
    <div class="gallery-container">
      <div class="gallery">
        <Swiper
          :initial-slide="currentSlide"
          :navigation="{
            nextEl: '.navigation__button--next',
            prevEl: '.navigation__button--prev'
          }"
          class="slider"
          keyboard
          @slide-change="onSlideChange"
        >
          <SwiperSlide
            v-for="(image, index) in props.images"
            :key="index"
            class="gallery__item swiper-slide"
          >
            <div class="image-container">
              <BaseImage
                :image="image.image"
                v-bind="
                  getImageSizes({
                    xs: { w: 375, h: 250 },
                    md: { w: 768, h: 513 },
                    lg: { w: 930, h: 620 }
                  })
                "
                class="gallery__image"
              />
            </div>
          </SwiperSlide>
        </Swiper>
      </div>
    </div>

    <div class="navigation grid">
      <Transition name="fade" mode="out-in">
        <p :key="activeSlideIndex" class="caption t-caption8">
          {{ props.images[activeSlideIndex].image.alt }}
        </p>
      </Transition>

      <div class="buttons">
        <div ref="swiperButtonPrev" class="navigation__button-container">
          <button class="navigation__button navigation__button--prev">
            <ArrowLeftIcon class="arrow-icon" />
          </button>
        </div>
        <div ref="swiperButtonNext" class="navigation__button-container">
          <button class="navigation__button navigation__button--next">
            <ArrowRightIcon class="arrow-icon" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Swiper as SwiperClass } from 'swiper/types'

import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?component'
import ArrowRightIcon from '@/assets/icons/arrow-right.svg?component'
import SwiperSlide from '@/components/Swiper/Slide.vue'
import Swiper from '@/components/Swiper/index.vue'
import type { PublicGalleryImage } from '@/service/__generated-api'
import 'swiper/css'

const props = withDefaults(
  defineProps<{
    images: PublicGalleryImage[]
    currentSlide?: number
  }>(),
  {
    currentSlide: 0
  }
)

const activeSlideIndex = ref(props.currentSlide)

const onSlideChange = (swiper: SwiperClass) => {
  activeSlideIndex.value = swiper.realIndex
}
</script>

<style lang="scss" scoped>
.lightbox {
  @include fill-space;
  display: grid;
  grid-template-rows: repeat(3, 1fr);

  @media only screen and (orientation: landscape) {
    @include center-content;
    @include center-self;
    max-width: 60%;
    height: auto;

    @include media-up($width: 1024px) {
      position: fixed;
      top: 0;
      right: 0;
      left: 0;
      display: grid;
      align-items: stretch;
      justify-content: stretch;
      max-width: calc(100% - rem(96px) * 2);
      height: 100%;
      margin: 0 auto;
      transform: none;
    }

    @include media-up(lg) {
      max-width: calc(100% - rem(218px) * 2);
    }
  }
}

.swiper-container {
  width: 100%;
}

.slider {
  width: 100%;
  max-height: calc($vh * 0.7);
  aspect-ratio: 3/2;
}

.gallery-container {
  display: flex;
  flex-direction: column;
  grid-row: 2;
  justify-content: center;
  user-select: none;

  @media only screen and (orientation: landscape) {
    @include size(100%);

    @include media-up($width: 1024px) {
      height: auto;
    }
  }

  &__item {
    @include center-content;
    flex-direction: column;
  }

  :deep(.image) {
    @include center-content;
  }
}

.gallery {
  position: relative;
}

.image-container {
  @include fill-space;
  @include center-content;
}

.image-wrapper {
  @include center-content;
  height: auto;
}

.caption {
  @include media-column(
    (
      xs: 24
    )
  );
  margin-top: rem(41px);
  color: $beige;
  text-align: center;

  @include media-up(md) {
    margin-top: rem(59px);
  }

  @media only screen and (orientation: landscape) {
    position: absolute;
    bottom: rem(37px);
    width: 100%;

    @include media-up($width: 1024px) {
      position: static;
      margin-block: rem(12px);
    }
  }
}

.navigation {
  grid-row: 3;

  @media only screen and (orientation: landscape) {
    @include size(100%);
    position: absolute;
    bottom: 0;
    z-index: 1;
    display: block;
    width: calc(100% + rem(60px));

    @include media-up($width: 1024px) {
      @include center-content;
      position: static;
      width: 100%;
    }
  }

  &__button {
    @include size(rem(60px));
    padding: rem(18px);
    color: $beige;
    background: $blue;
    border-radius: 50%;
    transition: opacity $transition;

    @include media-up(md) {
      @include size(rem(64px));
      padding: rem(20px);
    }

    &:disabled {
      opacity: 0;
    }
  }
}

.buttons {
  @include media-column(
    (
      xs: 14 5
    )
  );
  display: flex;
  align-self: flex-end;
  justify-content: space-between;
  margin-bottom: rem(95px);

  @include media-up($width: 500px) {
    margin-bottom: rem(15px);
  }

  @include media-up(md) {
    margin-bottom: rem(99px);
  }

  @media only screen and (orientation: landscape) {
    @include center-self;
    width: 100%;

    @include media-up($width: 1024px) {
      width: calc(100% + rem(62px));
    }

    @include media-up(lg) {
      width: calc(100% + rem(76px * 2) + rem(64px * 2));
    }
  }
}

.arrow-icon {
  @include size(100%);
}
</style>
